import type { UploadModule } from '@/api/files';
import type { StorageProfile } from '@/api/storage-admin';
import { valueAt, type FieldDef, type FieldOption, type FieldValues } from '@/components/fields';
import { MODULE_LABELS, whole, type FieldGroup } from './common';

const off = (path: string) => (v: FieldValues) => !valueAt(v, path);
const notS3 = (v: FieldValues) => v.mode !== 'S3';
const SIZE_UNITS = { units: ['MB', 'GB', 'TB'], unlimitedLabel: 'Unlimited' };

/** The operator's block: where files go and the ceilings a workspace's own rules cannot exceed. */
export function platformGroups(profiles: StorageProfile[]): FieldGroup[] {
  return [
    {
      title: 'Where files are kept',
      fields: [
        { type: 'select', name: 'mode', label: 'Storage', required: true, options: [
          { value: 'LOCAL', label: 'This server' },
          { value: 'S3', label: 'S3-compatible object storage' },
        ] },
        { type: 'select', name: 'active_profile_id', label: 'S3 server', required: true, hidden: notS3,
          config: { emptyLabel: 'Choose a server' },
          options: profiles.filter((p) => p.is_active).map((p) => ({ value: p.id, label: `${p.name} (${p.bucket})` })) },
        { type: 'text', name: 'key_prefix', label: 'Key prefix', disabled: true, hidden: (v) => !v.key_prefix,
          helperText: 'Fixed the first time S3 is switched on, so files never move under a new name.' },
        { type: 'switch', name: 'direct_upload', label: 'Browsers upload straight to S3', hidden: notS3,
          helperText: 'Saves this server the traffic; it falls back to uploading through the app when S3 is unreachable.' },
        { type: 'switch', name: 'local_fallback', label: 'When S3 is down, keep files here and send them later',
          hidden: notS3 },
        { type: 'switch', name: 'delete_local_after_sync', label: 'Delete this server’s copy once a file is in S3',
          hidden: notS3 },
        whole('keep_local_copy_hours', 'Keep this server’s copy for (hours)', 0, 720,
              { hidden: (v) => notS3(v) || !v.delete_local_after_sync }),
        { type: 'switch', name: 'tenant_can_manage_profiles', label: 'Workspace admins can manage S3 servers' },
      ],
    },
    {
      title: 'Ceilings',
      hint: 'The most this workspace may allow. Its own quotas and module rules can only be stricter.',
      fields: [
        { type: 'bytes', name: 'quota_ceiling_bytes', label: 'Total storage', config: SIZE_UNITS },
        { type: 'bytes', name: 'max_file_bytes_ceiling', label: 'Largest file', required: true,
          config: { units: ['MB', 'GB'] } },
        { type: 'tags', name: 'allowed_extensions_ceiling', label: 'File types allowed anywhere', grid: { xs: 12 },
          config: { normalise: 'extension' } },
      ],
    },
    {
      title: 'Retries and outages',
      hint: 'After this many S3 failures inside the window, uploads go to this server until the cool-down ends.',
      fields: [
        whole('breaker.failure_threshold', 'Failures before pausing S3', 1),
        whole('breaker.window_seconds', 'Within (seconds)', 10),
        whole('breaker.cooldown_seconds', 'Pause for (seconds)', 10),
        whole('retry.base_seconds', 'First retry after (seconds)', 1),
        whole('retry.max_backoff_seconds', 'Longest wait between retries (seconds)', 60),
        whole('retry.max_attempts', 'Give up after (attempts)', 1, 100),
        whole('retry.batch_size', 'Files per worker pass', 1, 500),
      ],
    },
    {
      title: 'Deleted items and clean-up',
      fields: [
        whole('soft_delete_days_max', 'Keep deleted items at most (days)', 0, 3650,
              { helperText: 'The longest a workspace or a master may keep removed files and deleted records.' }),
        { type: 'switch', name: 'delete_stray_files', label: 'Remove stray files', grid: { xs: 12 },
          helperText: 'Files in this workspace’s folder or S3 prefix that no record uses are deleted once older than '
            + 'the orphan period. Turn off if another installation shares this bucket prefix or upload folder.' },
      ],
    },
    {
      title: 'Background work and links',
      fields: [
        whole('worker.sweep_interval_minutes', 'Clean-up runs every (minutes)', 5),
        whole('worker.reconcile_hour_local', 'Nightly usage check at (hour, 0–23)', 0, 23),
        { type: 'text', name: 'worker.timezone', label: 'Time zone', required: true, placeholder: 'Asia/Kolkata' },
        whole('presign.upload_ttl_seconds', 'Upload links last (seconds)', 60, 3600),
        whole('presign.download_ttl_max_seconds', 'Download links last at most (seconds)', 30, 3600),
        whole('rate_limit.init_per_user_per_minute', 'Uploads a person can start per minute', 1, 10000),
      ],
    },
  ];
}

function moduleGroup(module: UploadModule): FieldGroup {
  const at = (key: string) => `modules.${module}.${key}`;
  return {
    title: MODULE_LABELS[module],
    fields: [
      { type: 'switch', name: at('enabled'), label: 'Uploads allowed', grid: { xs: 12 } },
      whole(at('max_file_mb'), 'Largest file (MB)', 1, 1024, { hidden: off(at('enabled')) }),
      whole(at('max_files_per_record'), 'Files per record', 1, 100, { hidden: off(at('enabled')) }),
      { type: 'tags', name: at('allowed_extensions'), label: 'File types', grid: { xs: 12 },
        config: { normalise: 'extension' }, hidden: off(at('enabled')) },
    ],
  };
}

/** The workspace ADMIN's block: quotas, per-module rules and the cost savers. */
export const TENANT_GROUPS: FieldGroup[] = [
  {
    title: 'Quotas',
    hint: 'Role and person policies override the default for the people they name.',
    fields: [
      { type: 'bytes', name: 'default_user_quota_bytes', label: 'Storage per person', config: SIZE_UNITS },
      { type: 'switch', name: 'employee_uploads_enabled', label: 'Employees can upload files' },
    ],
  },
  ...(Object.keys(MODULE_LABELS) as UploadModule[]).map(moduleGroup),
  {
    title: 'Saving space',
    fields: [
      { type: 'switch', name: 'image_compression.enabled', label: 'Shrink large photos before they upload',
        grid: { xs: 12 } },
      whole('image_compression.max_dimension_px', 'Longest side (px)', 320, 8000,
            { hidden: off('image_compression.enabled') }),
      { type: 'number', name: 'image_compression.jpeg_quality', label: 'JPEG quality (0.3–1)',
        validate: { min: 0.3, max: 1 }, config: { step: 0.01 }, hidden: off('image_compression.enabled') },
      { type: 'bytes', name: 'image_compression.min_bytes', label: 'Only photos larger than',
        config: { units: ['KB', 'MB'] }, hidden: off('image_compression.enabled') },
      { type: 'switch', name: 'dedup.enabled', label: 'Keep one copy of a file a person uploads twice',
        grid: { xs: 12 } },
      whole('dedup.max_hash_mb', 'Check files up to (MB)', 1, 1024, { hidden: off('dedup.enabled') }),
    ],
  },
  {
    title: 'Clean-up, downloads and logs',
    fields: [
      whole('soft_delete_days', 'Keep deleted items for (days)', 0, 3650,
            { helperText: 'Removed files and deleted records can be restored for this long. A master can set its own.' }),
      whole('orphan_days', 'Remove unsaved uploads after (days)', 1, 365),
      whole('download.ttl_seconds', 'Download links last (seconds)', 30, 3600),
      { type: 'select', name: 'download.default_disposition', label: 'Open files', required: true, options: [
        { value: 'inline', label: 'In the browser' },
        { value: 'attachment', label: 'As a download' },
      ] },
      { type: 'number', name: 'logging.download_sample_rate', label: 'Share of downloads logged (0–1)',
        validate: { min: 0, max: 1 }, config: { step: 0.05 } },
      whole('logging.retention_days', 'Keep logs for (days)', 7, 3650),
    ],
  },
];

const PROVIDERS: FieldOption[] = [
  { value: 'AWS', label: 'Amazon S3' },
  { value: 'MINIO', label: 'MinIO' },
  { value: 'WASABI', label: 'Wasabi' },
  { value: 'R2', label: 'Cloudflare R2' },
  { value: 'DO_SPACES', label: 'DigitalOcean Spaces' },
  { value: 'OTHER', label: 'Other S3-compatible' },
];

const keysHidden = (v: FieldValues) => v.credentials_mode === 'INSTANCE';
const notSts = (v: FieldValues) => v.isolation !== 'STS';

/** An S3 server. Secrets are write-only: the form only knows whether one is stored. */
export function profileFields(profile: StorageProfile | null): FieldDef[] {
  return [
    { type: 'text', name: 'name', label: 'Name', required: true, validate: { maxLength: 64 } },
    { type: 'select', name: 'provider', label: 'Provider', required: true, options: PROVIDERS,
      helperText: profile ? undefined : 'Picking one fills in its usual settings below.' },
    { type: 'text', name: 'endpoint_url', label: 'Endpoint URL', required: true, placeholder: 'https://s3.example.com',
      hidden: (v) => v.provider === 'AWS', validate: { pattern: '^https?://\\S+$', message: 'Start with http:// or https://.' } },
    { type: 'text', name: 'region', label: 'Region', placeholder: 'ap-south-1' },
    { type: 'text', name: 'bucket', label: 'Bucket', required: true, validate: {
      pattern: '^[a-z0-9][a-z0-9.\\-]{1,61}[a-z0-9]$',
      message: 'Lowercase letters, digits, dots and hyphens, 3 to 63 characters.',
    } },
    { type: 'select', name: 'credentials_mode', label: 'Credentials', required: true, options: [
      { value: 'KEYS', label: 'Access key' },
      { value: 'INSTANCE', label: 'This server’s own role (instance profile)' },
    ] },
    { type: 'secret', name: 'access_key_id', label: 'Access key ID', hidden: keysHidden,
      config: { isSet: profile?.access_key_id_set } },
    { type: 'secret', name: 'secret_access_key', label: 'Secret access key', hidden: keysHidden,
      config: { isSet: profile?.secret_access_key_set } },
    { type: 'select', name: 'isolation', label: 'Keeping workspaces apart', required: true, options: [
      { value: 'STS', label: 'Short-lived credentials limited to this workspace (STS)' },
      { value: 'PREFIX', label: 'Key prefix only' },
    ] },
    { type: 'text', name: 'sts_role_arn', label: 'Role ARN', required: true, hidden: notSts,
      placeholder: 'arn:aws:iam::123456789012:role/payroll-uploads' },
    { type: 'secret', name: 'sts_external_id', label: 'External ID', hidden: notSts,
      config: { isSet: profile?.sts_external_id_set } },
    whole('sts_duration_seconds', 'Credential lifetime (seconds)', 900, 43200, { hidden: notSts }),
    { type: 'select', name: 'sse', label: 'Encryption at rest', required: true, options: [
      { value: 'AES256', label: 'S3-managed keys (AES-256)' },
      { value: 'KMS', label: 'AWS KMS key' },
      { value: 'NONE', label: 'None' },
    ] },
    { type: 'text', name: 'kms_key_id', label: 'KMS key ID', required: true, hidden: (v) => v.sse !== 'KMS' },
    { type: 'select', name: 'storage_class', label: 'Storage class', config: { emptyLabel: 'Server default' },
      helperText: 'Intelligent-Tiering moves files nobody opens to cheaper tiers on its own.', options: [
        { value: 'STANDARD', label: 'Standard' },
        { value: 'INTELLIGENT_TIERING', label: 'Intelligent-Tiering' },
        { value: 'STANDARD_IA', label: 'Standard – Infrequent Access' },
        { value: 'ONEZONE_IA', label: 'One Zone – Infrequent Access' },
        { value: 'GLACIER_IR', label: 'Glacier Instant Retrieval' },
      ] },
    { type: 'switch', name: 'force_path_style', label: 'Path-style URLs (most self-hosted servers)' },
    { type: 'switch', name: 'checksum_supported', label: 'The server checks SHA-256 checksums' },
    { type: 'switch', name: 'is_active', label: 'Available for use' },
  ];
}

/** A new server's starting values; the provider's own defaults come from the server. */
export const NEW_PROFILE: FieldValues = { provider: 'AWS', credentials_mode: 'KEYS', sse: 'AES256', is_active: true };

export const ROLE_OPTIONS: FieldOption[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'PAYROLL_OFFICER', label: 'Payroll Officer' },
  { value: 'HR_MANAGER', label: 'HR Manager' },
  { value: 'FINANCE_HEAD', label: 'Finance Head' },
  { value: 'EMPLOYEE', label: 'Employee' },
];

/** A role's or one person's exceptions; a blank setting falls back to the workspace's rules. */
export function policyFields(users: FieldOption[]): FieldDef[] {
  return [
    { type: 'radio', name: 'scope_type', label: 'Applies to', required: true, grid: { xs: 12 }, options: [
      { value: 'ROLE', label: 'Everyone with a role' },
      { value: 'USER', label: 'One person' },
    ] },
    { type: 'select', name: 'role', label: 'Role', required: true, options: ROLE_OPTIONS,
      hidden: (v) => v.scope_type !== 'ROLE' },
    { type: 'select', name: 'user_id', label: 'Person', required: true, options: users,
      hidden: (v) => v.scope_type !== 'USER' },
    { type: 'bytes', name: 'quota_bytes', label: 'Storage', config: { units: ['MB', 'GB', 'TB'] },
      placeholder: 'Workspace default' },
    { type: 'bytes', name: 'max_file_bytes', label: 'Largest file', config: { units: ['MB', 'GB'] },
      placeholder: 'Module limits' },
    { type: 'tags', name: 'allowed_extensions', label: 'File types', grid: { xs: 12 },
      config: { normalise: 'extension' }, helperText: 'Leave empty to use each module’s list.' },
    { type: 'select', name: 'can_upload', label: 'Uploads', config: { emptyLabel: 'Same as everyone' }, options: [
      { value: true, label: 'Allowed' },
      { value: false, label: 'Not allowed' },
    ] },
    { type: 'text', name: 'note', label: 'Note', validate: { maxLength: 256 } },
  ];
}
